import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { DATABASES } from "../utils/databases";
import { downloadApiFile } from "../utils/download";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";
import { stripUuid } from "./approval/complianceExport";
import SecurityEventsLog from "./SecurityEventsLog";
import "./AuditLog.css";

const STATUSES = ["complete", "may_be_incomplete", "stopped", "no_sources", "direct", "error"];

// How many sources a row shows before collapsing the rest behind "+N more".
const SOURCES_PREVIEW =
  Number((window._env_ && window._env_.REACT_APP_AUDIT_SOURCES_PREVIEW) ||
    process.env.REACT_APP_AUDIT_SOURCES_PREVIEW) || 3;

const sourceLabel = (s) => {
  const name = stripUuid(s.document);
  return s.page_number != null ? `${name} p.${s.page_number}` : name;
};

/** A row's cited sources: UUID-stripped filenames (the full stored name stays in
 * the tooltip), collapsed to the first SOURCES_PREVIEW with a "+N more" toggle. */
function SourceList({ sources }) {
  const [expanded, setExpanded] = useState(false);
  const entries = (sources || []).filter((s) => sourceLabel(s).trim());
  if (entries.length === 0) return null;
  const hidden = entries.length - SOURCES_PREVIEW;
  const shown = expanded || hidden <= 0 ? entries : entries.slice(0, SOURCES_PREVIEW);
  return (
    <div className="audit-sources">
      <ul className="audit-source-list">
        {shown.map((s, i) => (
          <li key={i} title={s.document}>{sourceLabel(s)}</li>
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm audit-sources-toggle"
                aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}

/** Admin audit log (Norma feature 5): who asked what, when, with which
 * sources, disclaimer and status. Cursor-paginated; exports CSV / XLSX for the
 * current filters. Mirrors FeedbackReview.jsx's conventions. */
// "YYYY-MM-DD" (a local calendar day) -> ISO instant of its first / last millisecond.
function localDayBoundary(day, endOfDay) {
  const [year, month, date] = day.split("-").map(Number);
  const moment = endOfDay
    ? new Date(year, month - 1, date, 23, 59, 59, 999)
    : new Date(year, month - 1, date, 0, 0, 0, 0);
  return moment.toISOString();
}

function AnswerAuditLog() {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [database, setDatabase] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exporting, setExporting] = useState(null);  // "csv" | "xlsx" while an export is in flight

  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (database) params.set("database", database);
    if (userEmail.trim()) params.set("user_email", userEmail.trim());
    // Date pickers yield the viewer's local calendar days; send the instants
    // that bound them so "To" includes the whole day in the viewer's time zone.
    if (fromDate) params.set("created_from", localDayBoundary(fromDate, false));
    if (toDate) params.set("created_to", localDayBoundary(toDate, true));
    return params;
  }, [status, database, userEmail, fromDate, toDate]);

  // Bumped on every filter change and on unmount; a response is applied only
  // if the generation it was requested under is still current.
  const generation = useRef(0);

  const load = useCallback(async (cursor, gen) => {
    const params = filterParams();
    if (cursor) params.set("cursor", cursor);
    try {
      const data = await apiFetchJson(`/api/v1/audit/answers?${params.toString()}`);
      if (gen !== generation.current) return;
      setItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setNextCursor(data.next_cursor);
      setError("");
    } catch (err) {
      if (gen !== generation.current) return;
      setError(err.message);
    }
  }, [filterParams]);

  useEffect(() => {
    generation.current += 1;
    const gen = generation.current;
    setNextCursor(null);
    setLoading(true);
    load(null, gen).finally(() => {
      if (gen === generation.current) setLoading(false);
    });
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const loadMore = async () => {
    const gen = generation.current;
    setLoadingMore(true);
    try {
      await load(nextCursor, gen);
    } finally {
      setLoadingMore(false);
    }
  };

  const exportAs = async (format) => {
    setExporting(format);
    const params = filterParams();
    params.set("format", format);
    try {
      await downloadApiFile(`/api/v1/audit/answers/export?${params.toString()}`, `audit_answers.${format}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <p className="ds-page-subtitle">
        Who asked what and when, with the cited sources, editions, disclaimer and answer status.
      </p>

      <div className="ds-toolbar">
        <div className="ds-field">
          <label className="ds-label" htmlFor="audit-status">Status</label>
          <select id="audit-status" className="ds-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="audit-workspace">Workspace</label>
          <select id="audit-workspace" className="ds-select" value={database} onChange={(e) => setDatabase(e.target.value)}>
            <option value="">All</option>
            {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="audit-user-email">User email</label>
          <input id="audit-user-email" className="ds-input" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="audit-from">From</label>
          <input id="audit-from" className="ds-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="audit-to">To</label>
          <input id="audit-to" className="ds-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="audit-export">
          <button type="button" className="ds-btn ds-btn--secondary" disabled={exporting !== null}
                  onClick={() => exportAs("csv")}>
            {exporting === "csv" ? "Exporting…" : "Export CSV"}
          </button>
          <button type="button" className="ds-btn ds-btn--secondary" disabled={exporting !== null}
                  onClick={() => exportAs("xlsx")}>
            {exporting === "xlsx" ? "Exporting…" : "Export XLSX"}
          </button>
        </div>
      </div>

      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      {loading ? (
        <div className="ds-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="ds-empty">No audited answers match these filters</div>
      ) : (
        <div className="ds-table-wrap">
          <table className="ds-table audit-table">
            <thead>
              <tr>
                <th>Time</th><th>User</th><th>Workspace</th><th>Question</th>
                <th>Status</th><th>Sources</th><th>Editions</th><th>Disclaimer</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td className="audit-time">{new Date(it.created_at).toLocaleString()}</td>
                  <td>{it.user_email || it.user_id}</td>
                  <td>{it.database}</td>
                  <td className="audit-question" title={it.answer}>{it.question}</td>
                  <td><span className={statusBadgeClass(it.status)}>{statusLabel(it.status)}</span></td>
                  <td className="audit-sources-cell"><SourceList sources={it.sources} /></td>
                  <td className="audit-editions">
                    {(it.editions || []).map((e) => `${stripUuid(e.document)} [${e.edition}]`).join("; ")}
                  </td>
                  <td className="audit-disclaimer">{it.disclaimer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && !loading && (
        <button type="button" className="ds-btn ds-btn--secondary audit-load-more" onClick={loadMore}
                disabled={loadingMore}>
          Load more
        </button>
      )}
    </>
  );
}

// The audit views: label key -> view. Answers is the default.
const VIEWS = [
  { id: "answers", label: "audit.answersTab", Component: AnswerAuditLog },
  { id: "security", label: "audit.securityTab", Component: SecurityEventsLog },
];

export default function AuditLog() {
  const { t } = useTranslation();
  const [view, setView] = useState(VIEWS[0].id);
  const { Component } = VIEWS.find((v) => v.id === view);
  return (
    <div className="ds-page">
      <header className="ds-page-header">
        <h2 className="ds-page-title">Audit log</h2>
      </header>
      <div className="audit-views" role="group">
        {VIEWS.map((v) => (
          <button key={v.id} type="button" className="ds-chip" aria-pressed={view === v.id}
                  onClick={() => setView(v.id)}>
            {t(v.label)}
          </button>
        ))}
      </div>
      <Component />
    </div>
  );
}
